import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import AIInterviewSystem from '../components/AIInterviewSystem';
import { companyApi, interviewApi } from '../services/api';
import InvalidTokenPage from '../components/common/InvalidTokenPage';
import InterviewExpiredPage from '../components/common/InterviewExpiredPage';
import { isSupportedBrowser } from '../utils/browserCheck';

interface CandidatePageProps {
  companyId?: string;
  companyName?: string;
  interviewId?: string;
  candidateToken?: string;
}

interface CandidateInfo {
  candidateId: string;
  candidateName: string;
  position: string;
  industry: string;
  currentIndustry: string;
  currentPosition: string;
  isExperienced: boolean;
  candidateType: 'chuuto' | 'shinsotsu';
}

const CandidatePage: React.FC<CandidatePageProps> = ({
  companyId,
  companyName,
  interviewId,
  candidateToken,
}) => {
  const [candidateInfo, setCandidateInfo] = useState<CandidateInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [interviewExpired, setInterviewExpired] = useState(false);
  const [resolvedCompanyId, setResolvedCompanyId] = useState<string | undefined>(companyId);
  const [resolvedCompanyName, setResolvedCompanyName] = useState<string | undefined>(companyName);
  const [resolvedInterviewId, setResolvedInterviewId] = useState<string | undefined>(interviewId);

  useEffect(() => {
    const fetchCandidateInfo = async () => {
      // 新形式: トークンベースで面接情報を取得
      if (candidateToken) {
        try {
          setLoading(true);
          setError(null);
          setTokenInvalid(false);
          setInterviewExpired(false);

          const response = await interviewApi.getInterviewByToken(candidateToken);

          if (response.success && response.data) {
            const interviewData = response.data;

            // トークンから取得した企業・面接ID/名称を解決
            setResolvedCompanyId(interviewData.companyId);
            setResolvedCompanyName(interviewData.companyName);
            setResolvedInterviewId(interviewData.id);

            setCandidateInfo({
              candidateId: interviewData.candidateId,
              candidateName: interviewData.candidateName,
              position: interviewData.position,
              industry: interviewData.industry || '',
              currentIndustry: interviewData.currentIndustry || interviewData.industry || '',
              currentPosition: interviewData.currentPosition || interviewData.position,
              isExperienced: interviewData.isExperienced ?? true,
              candidateType: interviewData.candidateType ?? 'chuuto',
            });
          } else {
            if (response.error === 'invalid_candidate_token') {
              setTokenInvalid(true);
            } else if (response.error === 'interview_period_expired') {
              setInterviewExpired(true);
            } else {
              setError(`面接情報が見つかりません: ${response.error || '不明なエラー'}`);
            }
          }
        } catch (err) {
          console.error('面接者情報取得エラー:', err);
          setError(
            `面接者情報の取得に失敗しました: ${
              err instanceof Error ? err.message : '不明なエラー'
            }`
          );
        } finally {
          setLoading(false);
        }
        return;
      }

      // 旧形式: companyId / interviewId から面接情報を取得
      if (!companyId || !interviewId) {
        setError('企業IDまたは面接IDが不足しています');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await companyApi.getInterview(companyId, interviewId);

        if (response.success && response.data) {
          const interviewData = response.data;

          if (!interviewData.candidateName || !interviewData.position) {
            setError('面接者情報が不完全です。必要な情報：候補者名、応募職種');
            setLoading(false);
            return;
          }

          // 企業・面接ID/名称を解決
          setResolvedCompanyId(companyId);
          setResolvedCompanyName(companyName);
          setResolvedInterviewId(interviewId);

          setCandidateInfo({
            candidateId: interviewData.candidateId,
            candidateName: interviewData.candidateName,
            position: interviewData.position,
            industry: interviewData.industry || '',
            currentIndustry: interviewData.currentIndustry || interviewData.industry || '',
            currentPosition: interviewData.currentPosition || interviewData.position,
            isExperienced: interviewData.isExperienced ?? true,
            candidateType: interviewData.candidateType ?? 'chuuto',
          });
        } else {
          setError(`面接情報が見つかりません: ${response.error || '不明なエラー'}`);
        }
      } catch (err) {
        console.error('面接者情報取得エラー:', err);
        setError(
          `面接者情報の取得に失敗しました: ${err instanceof Error ? err.message : '不明なエラー'}`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateInfo();
  }, [companyId, companyName, interviewId, candidateToken]);

  // 非対応ブラウザチェック
  if (!isSupportedBrowser()) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fffbeb',
        }}
      >
        <div className="max-w-md mx-auto text-center p-6">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-yellow-900 mb-2">
            お使いのブラウザは対応していません
          </h2>
          <p className="text-yellow-800 mb-4">
            このシステムをご利用いただくには、以下の対応ブラウザをお使いください。
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
            <h4 className="font-medium text-yellow-900 mb-2">対応ブラウザ:</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Google Chrome</li>
            </ul>
          </div>
          <a
            href="https://www.google.com/chrome/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-4 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Google Chromeをダウンロード
          </a>
        </div>
      </Box>
    );
  }

  // ローディング中
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f9fafb',
        }}
      >
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">面接者情報を読み込み中...</h3>
          <p className="text-sm text-gray-500">候補者データを取得しています</p>
        </div>
      </Box>
    );
  }

  // エラー表示
  if (tokenInvalid) {
    return <InvalidTokenPage />;
  }

  if (interviewExpired) {
    return <InterviewExpiredPage />;
  }

  if (error || !candidateInfo) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fef2f2',
        }}
      >
        <div className="max-w-md mx-auto text-center p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-red-900 mb-2">面接者情報エラー</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
            <h4 className="font-medium text-red-900 mb-2">必要な情報:</h4>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• 候補者名</li>
              <li>• 応募職種</li>
              <li>• 企業ID: {resolvedCompanyId}</li>
              <li>• 面接ID: {resolvedInterviewId}</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            再読み込み
          </button>
        </div>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <AIInterviewSystem
        companyId={resolvedCompanyId}
        companyName={resolvedCompanyName}
        interviewId={resolvedInterviewId}
        candidateId={candidateInfo.candidateId}
        candidateName={candidateInfo.candidateName}
        position={candidateInfo.position}
        industry={candidateInfo.industry}
        currentIndustry={candidateInfo.currentIndustry}
        currentPosition={candidateInfo.currentPosition}
        isExperienced={candidateInfo.isExperienced}
        candidateType={candidateInfo.candidateType}
        candidateToken={candidateToken}
      />
    </Box>
  );
};

export default CandidatePage;
